import type { ServerToClientEvent } from '@atlassian/jira-s2c-event/src/index.tsx';

const ELEMENT_SELECTOR = 'script[data-clientjs]';
/**
 * We need to wait for slightly longer than 0ms. Waiting 4ms is enough
 * to guarantee the browser has processed any pending layout operations
 * that may yield further DOM content.
 */
const DOM_RENDER_MS = 4;

type ServerToClientEventHandler = (payload: ServerToClientEvent) => void;

export class ClientReceiver {
	#doc: Document;

	#fulfilled: Promise<void>;

	#onEvent: ServerToClientEventHandler;

	constructor(doc: Document, onEvent: ServerToClientEventHandler) {
		this.#doc = doc;
		this.#onEvent = onEvent;

		// consume any instructions that arrived before we initialised
		this.#doc
			.querySelectorAll(ELEMENT_SELECTOR)
			.forEach((el) => this.#handleServerToClientElement(el));

		// Bail early if DOM is already ready, otherwise observe for new instructions until it is.
		this.#fulfilled =
			this.#doc.readyState === 'complete'
				? Promise.resolve()
				: new Promise((resolve) => {
						this.#observe(resolve);
					});
	}

	#observe(resolve: () => void) {
		// observe the document for changes
		const observer = new MutationObserver((mutations) => this.#handleMutation(mutations));
		observer.observe(this.#doc.body, { attributes: false, childList: true, subtree: false });

		// only keep trying until DOM ready, then give up.
		this.#doc.addEventListener(
			'DOMContentLoaded',
			() => {
				setTimeout(() => {
					observer.disconnect();
					resolve();
				}, DOM_RENDER_MS + 1);
			},
			{ once: true },
		);
	}

	#handleMutation(mutations: MutationRecord[]) {
		for (const mutation of mutations) {
			if (mutation.type === 'childList') {
				for (const node of mutation.addedNodes) {
					if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
						if (node.matches(ELEMENT_SELECTOR)) {
							setTimeout(() => this.#handleServerToClientElement(node), DOM_RENDER_MS);
						}
					}
				}
			}
		}
	}

	#handleServerToClientElement(el: Element) {
		const events = el.textContent?.trim().split('\n');
		if (!events) {
			return;
		}
		for (const event of events) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const payload = JSON.parse(event) as ServerToClientEvent;
			this.#onEvent(payload);
		}
	}

	fulfilled() {
		return this.#fulfilled;
	}
}
